import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PalleteColor } from 'nanaykuna-ui-kit/loader';
import {
  BusinessLine,
  CreateOrUpdateOrderItem,
  Product,
  Variant,
  CustomConfigurationResponse,
  CustomConfigurationOption,
  CustomProductSelection,
  MixingCodeValidationResponse,
} from '@interfaces';
import { BusinessUnitId } from '@enums';
import { ProductService } from '@services';
import {
  CUSTOM_PRODUCT_ID,
  CUSTOM_PRODUCT_NAME,
  CUSTOM_PRODUCT_MEASUREMENT_UNIT,
  VALIDATION_ERROR_MESSAGES,
  HTTP_STATUS_NOT_FOUND,
} from './product-field.constants';

@Component({
  selector: 'unicon-product-field',
  templateUrl: './product-field.component.html',
  styleUrl: './product-field.component.scss',
})
export class ProductFieldComponent {
  @Input() initialValues?: CreateOrUpdateOrderItem;
  @Input() documentType!: string;
  @Input() verticalContainer!: boolean;
  @Input() initialBusinessLineId?: number;
  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() result: EventEmitter<CreateOrUpdateOrderItem> =
    new EventEmitter<CreateOrUpdateOrderItem>();
  protected form!: FormGroup;
  protected maxLength: number = 8;
  protected businessLines: BusinessLine[] = [];
  protected products: Product[] = [];
  protected variants: Variant[] = [];
  protected isLoading: boolean = false;
  protected isCustomProductMode: boolean = false;
  protected customConfigOptions: CustomConfigurationResponse | null = null;
  protected customSelection: CustomProductSelection = {
    cementId: null,
    resistanceId: null,
    additiveId: null,
    stoneId: null,
    slumpId: null,
  };
  protected validatedVariant: MixingCodeValidationResponse | null = null;
  protected customValidationError: string | null = null;
  protected isValidatingCustom: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private productService: ProductService,
  ) {
    this.setForm();
  }

  ngOnInit(): void {
    this.setInitialValuesAndValidations();
  }

  private setForm(): void {
    this.form = this.formBuilder.group({
      businessLineId: new FormControl<string>(
        { disabled: false, value: '' },
        Validators.compose([Validators.required]),
      ),
      productId: new FormControl<string>(
        { disabled: false, value: '' },
        Validators.compose([Validators.required]),
      ),
      productVariantId: new FormControl<string>(
        { disabled: false, value: '' },
        Validators.compose([Validators.required]),
      ),
      quantity: new FormControl<string>(
        { disabled: false, value: '' },
        Validators.compose([
          Validators.required,
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
        ]),
      ),
    });
  }

  private setInitialValuesAndValidations(): void {
    if (this.initialValues) {
      this.getBusinessLines(true);
      return;
    }

    // Si viene pre-configurado el businessLineId, cargar productos directamente
    if (this.initialBusinessLineId) {
      this.getBusinessLines();
      this.form.get('businessLineId')?.setValue(this.initialBusinessLineId);
      this.form.get('businessLineId')?.disable();
      this.getProducts(this.initialBusinessLineId);
      return;
    }

    this.getBusinessLines();
  }

  private getBusinessLines(isEdit = false): void {
    this.productService.getBusinessLines().subscribe({
      next: (businessLines: BusinessLine[]) => {
        this.businessLines = businessLines;
        if (isEdit && this.initialValues?.businessLineId) {
          this.getProducts(this.initialValues?.businessLineId, true);
        }
      },
    });
  }

  protected onBusinessLineId(event: Event): void {
    const businessLineId: number = parseInt(
      (event.target as HTMLSelectElement).value,
      10,
    );
    if (businessLineId) {
      this.getProducts(businessLineId, false, true);
    } else {
      this.form.controls['productId'].setValue('');
      this.form.controls['productVariantId'].setValue('');
    }
  }

  private getProducts(
    businessLineId: number,
    isEdit = false,
    reset = false,
  ): void {
    this.productService.getProducts(businessLineId).subscribe({
      next: (products: Product[]) => {
        this.products = products;

        // Add "Personalizado" option if CONCRETE business unit
        if (businessLineId === BusinessUnitId.CONCRETE) {
          this.products.push({
            id: CUSTOM_PRODUCT_ID,
            name: CUSTOM_PRODUCT_NAME,
            measurementUnitName: CUSTOM_PRODUCT_MEASUREMENT_UNIT,
          });
        }

        if (isEdit && this.initialValues?.productId) {
          this.getVariants(this.initialValues.productId, true);
        } else if (isEdit && this.initialValues) {
          this.form.patchValue(this.initialValues);
          this.cdRef.detectChanges();
        }
        if (reset && !isEdit) {
          this.form.controls['productId'].setValue('');
          this.form.controls['productVariantId'].setValue('');
        }
      },
    });
  }

  protected onProductId(event: Event): void {
    const productId: string = (event.target as HTMLSelectElement).value;
    const businessLineId: number = parseInt(
      this.form.get('businessLineId')?.value,
      10,
    );

    // Check if "Personalizado" option selected
    if (
      productId === CUSTOM_PRODUCT_ID &&
      businessLineId === BusinessUnitId.CONCRETE
    ) {
      this.isCustomProductMode = true;
      this.form.get('productVariantId')?.disable();
      this.loadCustomConfiguration();
      return;
    }

    this.isCustomProductMode = false;
    this.form.get('productVariantId')?.enable();
    this.resetCustomSelection();

    if (productId) {
      this.getVariants(productId, false, true);
      return;
    }

    this.form.controls['productVariantId'].setValue('');
  }

  private getVariants(productId: string, isEdit = false, reset = false): void {
    this.productService.getVariants(productId).subscribe({
      next: (variants: Variant[]) => {
        this.variants = variants;

        if (isEdit && this.initialValues) {
          // Check if the variant exists in the loaded list
          const variantExists = this.variants.some(
            v => v.id === this.initialValues?.productVariantId,
          );

          if (
            !variantExists &&
            this.initialValues.businessLineId === BusinessUnitId.CONCRETE &&
            this.initialValues.mixingCode
          ) {
            // This is a custom product - switch to custom mode
            this.loadCustomProductForEdit();
          } else {
            // Regular product - set form values normally
            this.form.setValue(this.initialValues);
            this.cdRef.detectChanges();
          }
        }

        if (!isEdit && reset) {
          this.form.controls['productVariantId'].setValue('');
        }
      },
    });
  }

  protected getErrorState(inputName: string): boolean {
    return (this.form.get(inputName)?.touched &&
      this.form.get(inputName)?.hasError('required')) as boolean;
  }

  protected getFormIconColor(inputName: string): PalleteColor {
    if (this.getErrorState(inputName)) {
      return 'critical-300';
    }

    return 'neutrals-900';
  }

  protected onSubmit(): void {
    const formattedQuantity = this.formatQuantity(this.form.value['quantity']);

    // Obtener businessLineId (puede estar deshabilitado si viene pre-configurado)
    const businessLineId =
      this.initialBusinessLineId ??
      parseInt(this.form.get('businessLineId')?.value.toString(), 10);

    if (this.isCustomProductMode && this.validatedVariant) {
      const orderItem: CreateOrUpdateOrderItem = {
        businessLineId,
        productId: this.validatedVariant.productId,
        productVariantId: this.validatedVariant.id,
        quantity: formattedQuantity,
      };
      this.result.emit(orderItem);
      return;
    }

    const orderItem: CreateOrUpdateOrderItem = {
      ...this.form.value,
      businessLineId,
      quantity: formattedQuantity,
    };
    this.result.emit(orderItem);
  }

  private formatQuantity(quantity: string): string {
    const parts = quantity.split('.');
    return parts.length === 2
      ? `${parts[0]}.${parts[1].substring(0, 1)}`
      : parts[0];
  }

  private loadCustomConfiguration(): void {
    this.productService.getCustomConfiguration().subscribe({
      next: (config: CustomConfigurationResponse) => {
        this.customConfigOptions = config;
      },
      error: error => {
        console.error('Error loading custom configuration:', error);
        this.customValidationError =
          'Error al cargar las opciones de configuración.';
      },
    });
  }

  private resetCustomSelection(): void {
    this.customSelection = {
      cementId: null,
      resistanceId: null,
      additiveId: null,
      stoneId: null,
      slumpId: null,
    };
    this.resetValidation();
  }

  protected onCustomAttributeChange(
    attribute: keyof CustomProductSelection,
  ): void {
    this.resetSelectionsAfter(attribute);
    this.resetValidation();

    if (this.isAllCustomAttributesSelected()) {
      this.validateCustomMixingCode();
    }
  }

  private resetSelectionsAfter(attribute: keyof CustomProductSelection): void {
    const attributes: (keyof CustomProductSelection)[] = [
      'cementId',
      'resistanceId',
      'additiveId',
      'stoneId',
      'slumpId',
    ];
    const index = attributes.indexOf(attribute);
    for (let i = index + 1; i < attributes.length; i++) {
      this.customSelection[attributes[i]] = null;
    }
  }

  private resetValidation(): void {
    this.validatedVariant = null;
    this.customValidationError = null;
  }

  private isAllCustomAttributesSelected(): boolean {
    return (
      this.customSelection.cementId !== null &&
      this.customSelection.resistanceId !== null &&
      this.customSelection.additiveId !== null &&
      this.customSelection.stoneId !== null &&
      this.customSelection.slumpId !== null
    );
  }

  private buildMixingCode(): string {
    const cement = this.getCodeValue('cement', this.customSelection.cementId);
    const resistance = this.getCodeValue(
      'resistance',
      this.customSelection.resistanceId,
    );
    const additive = this.getCodeValue(
      'additive',
      this.customSelection.additiveId,
    );
    const stone = this.getCodeValue('stone', this.customSelection.stoneId);
    const slump = this.getCodeValue('slump', this.customSelection.slumpId);

    return `${cement}${resistance}${additive}${stone}${slump}`;
  }

  private getCodeValue(attribute: string, id: string | null): string {
    if (!id || !this.customConfigOptions) return '';

    const options =
      this.customConfigOptions[attribute as keyof CustomConfigurationResponse];
    const option = options.find(opt => opt.id === id);
    return option?.codeValue || '';
  }

  private validateCustomMixingCode(): void {
    const mixingCode = this.buildMixingCode();
    this.isValidatingCustom = true;
    this.resetValidation();

    this.productService.validateMixingCode(mixingCode).subscribe({
      next: (response: MixingCodeValidationResponse) => {
        this.validatedVariant = response;
        this.isValidatingCustom = false;
      },
      error: error => {
        this.customValidationError =
          error.status === HTTP_STATUS_NOT_FOUND
            ? VALIDATION_ERROR_MESSAGES.NOT_AVAILABLE
            : VALIDATION_ERROR_MESSAGES.GENERAL_ERROR;
        this.isValidatingCustom = false;
      },
    });
  }

  private loadCustomProductForEdit(): void {
    if (!this.initialValues?.mixingCode) {
      console.error('Cannot load custom product: missing mixingCode');
      return;
    }

    this.isCustomProductMode = true;

    // Set form values including businessLineId
    this.form
      .get('businessLineId')
      ?.setValue(this.initialValues.businessLineId);
    this.form.get('productId')?.setValue(CUSTOM_PRODUCT_ID);
    this.form.get('productVariantId')?.disable();

    // Set the quantity
    if (this.initialValues.quantity) {
      this.form.get('quantity')?.setValue(this.initialValues.quantity);
    }

    // Load custom configuration options
    this.productService.getCustomConfiguration().subscribe({
      next: (config: CustomConfigurationResponse) => {
        this.customConfigOptions = config;

        // Decompose the mixing code to populate custom selections
        this.decomposeMixingCode(this.initialValues!.mixingCode!);

        // Create validated variant object from initial values
        this.validatedVariant = {
          id: this.initialValues!.productVariantId,
          name: this.initialValues!.productVariantName || '',
          mixingCode: this.initialValues!.mixingCode!,
          productId: this.initialValues!.productId,
          measurementUnitName: CUSTOM_PRODUCT_MEASUREMENT_UNIT,
        };
      },
      error: error => {
        console.error('Error loading custom configuration for edit:', error);
        this.customValidationError =
          VALIDATION_ERROR_MESSAGES.LOAD_CONFIG_ERROR;
      },
    });
  }

  private decomposeMixingCode(mixingCode: string): void {
    if (!this.customConfigOptions) {
      return;
    }

    // The mixing code is a concatenation of all codeValues
    // We need to match each segment against the configuration options
    // Since the lengths vary, we'll try to find the best match

    // Helper function to find option ID by matching code value in mixing code
    const findOptionIdByCode = (
      options: CustomConfigurationOption[],
    ): string | null => {
      // Try to find an option whose codeValue is contained in the mixingCode
      // Sort by codeValue length (longest first) to match longer codes first
      const sorted = [...options].sort(
        (a, b) => b.codeValue.length - a.codeValue.length,
      );

      for (const option of sorted) {
        if (mixingCode.includes(option.codeValue)) {
          return option.id;
        }
      }
      return null;
    };

    // Populate custom selections by matching code values
    this.customSelection.cementId = findOptionIdByCode(
      this.customConfigOptions.cement,
    );
    this.customSelection.resistanceId = findOptionIdByCode(
      this.customConfigOptions.resistance,
    );
    this.customSelection.additiveId = findOptionIdByCode(
      this.customConfigOptions.additive,
    );
    this.customSelection.stoneId = findOptionIdByCode(
      this.customConfigOptions.stone,
    );
    this.customSelection.slumpId = findOptionIdByCode(
      this.customConfigOptions.slump,
    );
  }

  protected isFormValid(): boolean {
    if (this.isCustomProductMode) {
      return (
        this.validatedVariant !== null &&
        this.form.get('quantity')?.valid === true
      );
    }
    return this.form.valid;
  }
}
